import { useCallback, useEffect, useRef, useState } from 'react'

import styles from './styles.module.scss'

import { isApiError } from '@/api'
import {
  getAnalyticsEvents,
  getGlobalProperties,
  getPublishPreview,
  publishAnalyticsRegistry,
  saveAnalyticsEvents,
  saveGlobalProperties,
} from '@/api/analyticsEvents'
import DiffPreview from '@/pages/app/AnalyticsPortal/components/DiffPreview/DiffPreview'
import EventEditor from '@/pages/app/AnalyticsPortal/components/EventEditor/EventEditor'
import EventsTable from '@/pages/app/AnalyticsPortal/components/EventsTable/EventsTable'
import GlobalPropertiesPanel from '@/pages/app/AnalyticsPortal/components/GlobalPropertiesPanel/GlobalPropertiesPanel'
import PublishPanel from '@/pages/app/AnalyticsPortal/components/PublishPanel/PublishPanel'
import { NEW_EVENT_ID_PREFIX } from '@/pages/app/AnalyticsPortal/constants'
import type { AnalyticsEvent, GlobalProperty, PublishPreviewSnapshot } from '@/pages/app/AnalyticsPortal/types'
import { computePublishDiff, computeSaveDiff } from '@/pages/app/AnalyticsPortal/utils'

const AnalyticsPortalPage = () => {
  const [events, setEvents] = useState<AnalyticsEvent[]>([])
  const [globalPropertiesEvent, setGlobalPropertiesEvent] = useState<AnalyticsEvent | null>(null)
  const [pendingChanges, setPendingChanges] = useState<Map<string, Partial<AnalyticsEvent>>>(new Map())
  const [pendingGlobalPropertiesChanges, setPendingGlobalPropertiesChanges] = useState<Record<
    string,
    GlobalProperty
  > | null>(null)
  const [editingEvent, setEditingEvent] = useState<AnalyticsEvent | null>(null)
  const [loading, setLoading] = useState({
    events: true,
    globalProps: true,
    saving: false,
    publishing: false,
    fetchingPreview: false,
  })
  const [error, setError] = useState<string | null>(null)
  const [showSavePreview, setShowSavePreview] = useState(false)
  const [showPublishPreview, setShowPublishPreview] = useState(false)
  const [publishPreviewSnapshot, setPublishPreviewSnapshot] = useState<PublishPreviewSnapshot | null>(null)

  const loadRequestIdRef = useRef(0)

  const loadData = useCallback(async (): Promise<boolean> => {
    const requestId = ++loadRequestIdRef.current
    setLoading((l) => ({ ...l, events: true, globalProps: true }))
    let eventsLoaded = false

    try {
      const eventsRes = await getAnalyticsEvents()
      if (requestId === loadRequestIdRef.current) {
        setEvents(eventsRes.data)
        eventsLoaded = true
      }
    } catch (err) {
      console.error('Failed to load events:', err)
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading((l) => ({ ...l, events: false }))
      }
    }

    try {
      const globalRes = await getGlobalProperties()
      if (requestId === loadRequestIdRef.current) {
        setGlobalPropertiesEvent(globalRes.data)
      }
    } catch (err) {
      console.warn('Failed to load global properties:', err)
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading((l) => ({ ...l, globalProps: false }))
      }
    }

    return eventsLoaded
  }, [])

  const partitionPendingChanges = () => {
    const creates: Partial<AnalyticsEvent>[] = []
    const updates: Partial<AnalyticsEvent>[] = []
    const createIds: string[] = []
    const updateIds: string[] = []
    const eventFields = (e: Partial<AnalyticsEvent>) => ({
      name: e.name,
      description: e.description,
      trigger: e.trigger,
      properties_schema: e.properties_schema,
      platforms: e.platforms,
      lifecycle_state: e.lifecycle_state,
    })
    pendingChanges.forEach((e, id) => {
      if (id.startsWith(NEW_EVENT_ID_PREFIX)) {
        creates.push({ event_key: e.event_key, ...eventFields(e) })
        createIds.push(id)
      } else {
        updates.push({ id, ...eventFields(e) })
        updateIds.push(id)
      }
    })
    return { creates, updates, createIds, updateIds }
  }

  const saveEvents = async (): Promise<{ errors: string[]; failedIds: string[] }> => {
    if (!pendingChanges.size) return { errors: [], failedIds: [] }
    const { creates, updates, createIds, updateIds } = partitionPendingChanges()
    try {
      const res = await saveAnalyticsEvents(creates, updates)
      const failedIds: string[] = []
      if (res.data.errors.length) {
        res.data.errors.forEach((errorMsg: string) => {
          ;[...createIds, ...updateIds].forEach((id) => {
            const event = pendingChanges.get(id)
            if (event?.name && errorMsg.includes(event.name)) failedIds.push(id)
          })
        })
      }
      return { errors: res.data.errors, failedIds }
    } catch (err) {
      console.error('Failed to save events:', err)
      const allIds = [...createIds, ...updateIds]
      return {
        errors: [isApiError(err) ? err.detail || err.message : 'Failed to save events'],
        failedIds: allIds,
      }
    }
  }

  const saveGlobalProps = async (): Promise<string[]> => {
    if (!pendingGlobalPropertiesChanges) return []
    try {
      await saveGlobalProperties(pendingGlobalPropertiesChanges)
      return []
    } catch (err) {
      console.error('Failed to save global properties:', err)
      return [isApiError(err) ? err.detail || err.message : 'Failed to save global properties']
    }
  }

  const handleSave = async () => {
    if (!pendingChanges.size && !pendingGlobalPropertiesChanges) return
    setLoading((l) => ({ ...l, saving: true }))
    setError(null)
    try {
      const [eventResult, globalPropsErrors] = await Promise.all([saveEvents(), saveGlobalProps()])
      const allErrors = [...eventResult.errors, ...globalPropsErrors]

      const eventsLoaded = await loadData()
      if (eventsLoaded) {
        if (eventResult.failedIds.length > 0) {
          setPendingChanges((prev) => {
            const newChanges = new Map<string, Partial<AnalyticsEvent>>()
            eventResult.failedIds.forEach((id) => {
              const change = prev.get(id)
              if (change) newChanges.set(id, change)
            })
            return newChanges
          })
        } else {
          setPendingChanges(new Map())
        }
        if (globalPropsErrors.length === 0) {
          setPendingGlobalPropertiesChanges(null)
        }
      }
      if (allErrors.length) setError(allErrors.join(', '))
    } catch (err) {
      console.error('Failed to save:', err)
      setError(isApiError(err) ? err.detail || err.message : 'Failed to save changes')
    } finally {
      setLoading((l) => ({ ...l, saving: false }))
    }
  }

  const handleCreateEvent = () => {
    setEditingEvent({
      id: `new-${crypto.randomUUID()}`,
      event_key: '',
      name: '',
      description: null,
      trigger: null,
      properties_schema: {},
      platforms: [],
      lifecycle_state: 'draft',
      created_at: new Date().toISOString(),
      modified_at: new Date().toISOString(),
      created_by: '',
      created_by_name: '',
      updated_by: '',
      updated_by_name: '',
      last_published_at: null,
      status: 'needs_publish',
    })
  }

  const handleEventUpdate = (event: AnalyticsEvent) => {
    setPendingChanges((prev) => new Map(prev).set(event.id, event))
    setEditingEvent(null)
  }

  const handleDeprecateEvent = (event: AnalyticsEvent) => {
    setPendingChanges((prev) => new Map(prev).set(event.id, { ...event, lifecycle_state: 'deprecated' }))
  }

  const handleUndeprecateEvent = (event: AnalyticsEvent) => {
    const originalEvent = events.find((e) => e.id === event.id)
    const originalLifecycle = originalEvent?.lifecycle_state ?? 'draft'
    setPendingChanges((prev) => {
      const newChanges = new Map(prev)
      const existingChange = newChanges.get(event.id)
      if (existingChange) {
        newChanges.set(event.id, { ...existingChange, lifecycle_state: originalLifecycle })
      }
      return newChanges
    })
  }

  const handleShowPublishPreview = async () => {
    setLoading((l) => ({ ...l, fetchingPreview: true }))
    setError(null)
    try {
      const response = await getPublishPreview()
      const changedEvents = response.data.events.filter((e) => e.change_type !== 'unchanged')
      const hasGlobalPropsToPublish = response.data.global_properties_changed

      if (changedEvents.length === 0 && !hasGlobalPropsToPublish) {
        setError('No changes to publish')
        setShowPublishPreview(false) // close the dialog
        return
      }
      setPublishPreviewSnapshot(response.data)
      setShowPublishPreview(true)
    } catch (err: unknown) {
      console.error('Failed to get publish preview:', err)
      const errorDetail = isApiError(err) ? err.detail || err.message : 'Failed to get publish preview'
      setError(errorDetail)
    } finally {
      setLoading((l) => ({ ...l, fetchingPreview: false }))
    }
  }

  const handlePublish = async () => {
    setLoading((l) => ({ ...l, publishing: true }))
    setError(null)
    try {
      await publishAnalyticsRegistry()
      await loadData()
      setShowPublishPreview(false)
      setPublishPreviewSnapshot(null)
    } catch (err: unknown) {
      console.error('Failed to publish:', err)
      const errorDetail = isApiError(err) ? err.detail || err.message : 'Failed to publish changes'
      if (errorDetail === 'No changes to publish') {
        setShowPublishPreview(false)
        setPublishPreviewSnapshot(null)
      } else {
        setError(errorDetail)
      }
    } finally {
      setLoading((l) => ({ ...l, publishing: false }))
    }
  }

  useEffect(() => {
    loadData()
  }, [loadData])

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitleSection}>
          <h1 className={styles.title}>Analytics Portal</h1>
          <span className={styles.subtitle}>Review and save changes made to Global Properties and Analytics Events, then publish for engineering to implement.</span>
        </div>
        <div className={styles.headerActions}>
          <PublishPanel
            hasChanges={pendingChanges.size > 0 || pendingGlobalPropertiesChanges !== null}
            isSaving={loading.saving}
            isFetchingPreview={loading.fetchingPreview}
            isPublishing={loading.publishing}
            onSave={() => setShowSavePreview(true)}
            onPublish={handleShowPublishPreview}
          />
        </div>
      </div>
      {error && <div className={styles.errorBanner}>{error}</div>}
      <GlobalPropertiesPanel
        globalPropertiesEvent={globalPropertiesEvent}
        isLoading={loading.globalProps}
        pendingChanges={pendingGlobalPropertiesChanges}
        onPendingChangesUpdate={setPendingGlobalPropertiesChanges}
      />
      <EventsTable
        events={events}
        isLoading={loading.events}
        onEditEvent={setEditingEvent}
        onCreateEvent={handleCreateEvent}
        onDeprecateEvent={handleDeprecateEvent}
        onUndeprecateEvent={handleUndeprecateEvent}
        pendingChanges={pendingChanges}
      />
      {editingEvent && (
        <EventEditor
          event={editingEvent}
          existingNames={[
            ...events.filter((e) => e.id !== editingEvent.id).map((e) => e.name),
            ...Array.from(pendingChanges.entries())
              .filter(([id]) => id.startsWith('new-') && id !== editingEvent.id)
              .map(([, e]) => e.name)
              .filter((name): name is string => !!name),
          ]}
          onSave={handleEventUpdate}
          onCancel={() => setEditingEvent(null)}
        />
      )}
      {showSavePreview &&
        (() => {
          const { eventChanges, propertyChanges } = computeSaveDiff(
            pendingChanges,
            events,
            pendingGlobalPropertiesChanges,
            globalPropertiesEvent
          )
          return (
            <DiffPreview
              mode="save"
              title="Review Changes Before Saving"
              eventChanges={eventChanges}
              propertyChanges={propertyChanges}
              isLoading={loading.saving}
              onConfirm={async () => {
                await handleSave()
                setShowSavePreview(false)
              }}
              onCancel={() => setShowSavePreview(false)}
            />
          )
        })()}
      {showPublishPreview &&
        publishPreviewSnapshot &&
        (() => {
          const { eventChanges, propertyChanges } = computePublishDiff(publishPreviewSnapshot, globalPropertiesEvent)
          return (
            <DiffPreview
              mode="publish"
              title="Review Changes Before Publishing"
              eventChanges={eventChanges}
              propertyChanges={propertyChanges}
              isLoading={loading.publishing}
              onConfirm={handlePublish}
              onCancel={() => {
                setShowPublishPreview(false)
                setPublishPreviewSnapshot(null)
              }}
            />
          )
        })()}
    </div>
  )
}

export default AnalyticsPortalPage
